
import { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  // Check if we're on a page that needs special navbar treatment
  const isDarkPage = location.pathname === '/paket-pris';
  const shouldShowBackground = isScrolled || (!isDarkPage && !isScrolled);

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-md py-3' 
          : isDarkPage 
            ? 'bg-black/20 backdrop-blur-sm py-5' 
            : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <img 
            src="/lovable-uploads/1789bb12-3853-4a6f-85ed-b0a660e3bf2d.png" 
            alt="Story Store Logo" 
            className="h-10 mr-2" 
          />
          <span className={`font-heading font-bold text-xl ${
            isScrolled ? 'text-storystore-blue' : isDarkPage ? 'text-white' : 'text-white'
          }`}>
            STORY STORE
          </span>
        </Link>
        
        <nav className="hidden md:flex items-center space-x-8">
          <NavLinks isScrolled={isScrolled} />
          
          <Link
            to="/#kontakt" 
            className="btn-primary"
            onClick={() => {
              if (location.pathname !== '/') {
                window.location.href = '/#kontakt';
              } else {
                const targetElement = document.querySelector('#kontakt');
                if (targetElement) {
                  targetElement.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
          >
            Kontakta oss
          </Link>
        </nav>
        
        <button 
          className={`md:hidden transition-colors ${
            isScrolled ? 'text-storystore-blue' : isDarkPage ? 'text-white' : 'text-storystore-blue'
          }`}
          onClick={toggleMenu}
          aria-label="Huvudmeny"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      
      <div 
        className={`md:hidden fixed inset-0 bg-white z-40 transition-transform duration-300 ease-in-out ${
          isMenuOpen ? 'translate-y-0' : '-translate-y-full'
        } pt-20`}
      >
        <div className="container mx-auto px-6 py-6 flex flex-col space-y-8">
          <NavLinks isScrolled={true} onClick={() => setIsMenuOpen(false)} isMobile={true} />
          
          <Link
            to="/#kontakt" 
            className="btn-primary text-center"
            onClick={() => {
              setIsMenuOpen(false);
              if (location.pathname !== '/') {
                window.location.href = '/#kontakt';
              } else {
                const targetElement = document.querySelector('#kontakt');
                if (targetElement) {
                  targetElement.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
          >
            Kontakta oss
          </Link>
        </div>
      </div>
    </header>
  );
};

// Memoize NavLinks to prevent unnecessary re-renders
interface NavLinksProps {
  isScrolled: boolean;
  onClick?: () => void;
  isMobile?: boolean;
}

const NavLinks = memo(({ isScrolled, onClick, isMobile = false }: NavLinksProps) => {
  const location = useLocation();
  const linkClass = `font-medium hover:text-storystore-red transition-colors duration-300 ${
    isScrolled || isMobile ? 'text-storystore-blue' : 'text-white'
  } ${isMobile ? 'text-2xl block py-3' : ''}`;

  const links = [
    { href: "#tjanster", label: "Tjänster", type: "hash" },
    { href: "#portfolio", label: "Portfolio", type: "hash" },
    { href: "#process", label: "Process", type: "hash" },
    { href: "#malgrupper", label: "Målgrupper", type: "hash" },
    { href: "#kundcase", label: "Kunder", type: "hash" },
    { href: "#om-oss", label: "Om oss", type: "hash" },
    { href: "/paket-pris", label: "Paket & Priser", type: "route" },
  ];

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      // Navigate to home page first, then scroll to section
      window.location.href = '/' + href;
    } else {
      // Already on home page, just scroll to section
      const targetElement = document.querySelector(href);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
    if (onClick) onClick();
  };

  return (
    <>
      {links.map((link) => {
        if (link.type === 'route') {
          return (
            <Link
              key={link.href}
              to={link.href}
              className={linkClass}
              onClick={onClick}
            >
              {link.label}
            </Link>
          );
        } else {
          return (
            <a
              key={link.href}
              href={link.href}
              className={linkClass}
              onClick={(e) => handleHashClick(e, link.href)}
            >
              {link.label}
            </a>
          );
        }
      })}
    </>
  );
});

NavLinks.displayName = 'NavLinks';

export default Header;
